import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import FullscreenMedia from "@/components/FullscreenMedia";
import VideoGallery from "@/components/VideoGallery";
import { displayAuthorName } from "@/lib/anonAuthor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, MessagesSquare, ShieldCheck, Send, CheckCircle2, AlertTriangle, RefreshCw, XCircle, PackageCheck, Hand, Map as MapIconLucide, Sparkles, Heart, Navigation, Expand, Camera, Loader2, X, Flag, ThumbsUp, ThumbsDown } from "lucide-react";
import { ReportUserButton } from "@/components/ReportUserDialog";
import ShareButton from "@/components/ShareButton";
import { CommentLikeButton, CommentSortSelect } from "@/components/CommentSocial";
import { ReportContentButton } from "@/components/ReportContentDialog";
import AdminReportActionsBar from "@/components/AdminReportActionsBar";

const RISK_COLORS = { green: "#10B981", yellow: "#EAB308", red: "#EF4444" };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang, fmtDateTime } = useI18n();
  const [report, setReport] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [commentSort, setCommentSort] = useState(() => {
    try { return localStorage.getItem("tc_comment_sort") || "recent"; } catch { return "recent"; }
  });
  const chatRef = useRef(null);
  const justCreated = new URLSearchParams(location.search).get("new") === "1";

  const [notFound, setNotFound] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      setReport(data);
      const { data: msgs } = await api.get(`/reports/${id}/messages`, { params: { sort: commentSort } }).catch(() => ({ data: [] }));
      setMessages(msgs);
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) {
        setNotFound(true);
        setReport(null);
      }
    }
  };

  useEffect(() => { load(); const it = setInterval(load, 10000); return () => clearInterval(it); }, [id, commentSort]);
  useEffect(() => { try { localStorage.setItem("tc_comment_sort", commentSort); } catch {} }, [commentSort]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/reports/${id}/messages`, { text });
      setText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("not_authenticated"));
    }
  };

  const [confirmChange, setConfirmChange] = useState(null); // { newKind, current }
  const myConfirmation = React.useMemo(() => {
    const log = report?.confirmations_log || [];
    if (!user) return null;
    return log.filter((c) => c.user_id === user.user_id && !c.superseded_at).pop() || null;
  }, [report, user]);

  const confirm = async (newKind) => {
    // If user already has a different active confirmation, ask before replacing
    if (myConfirmation && myConfirmation.kind && myConfirmation.kind !== newKind && newKind !== "undo") {
      setConfirmChange({ newKind, current: myConfirmation.kind });
      return;
    }
    try {
      await api.post(`/reports/${id}/confirm`, { kind: newKind });
      toast.success(t("saved_confirmation"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    }
  };

  const confirmChangeAccept = async () => {
    if (!confirmChange) return;
    const k = confirmChange.newKind; setConfirmChange(null);
    try {
      await api.post(`/reports/${id}/confirm`, { kind: k });
      toast.success(t("saved_confirmation"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    }
  };

  const takeReusable = async () => {
    try { await api.post(`/reports/${id}/reusable-taken`); toast.success(t("thanks")); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="report-not-found">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="text-5xl mb-3">🕳️</div>
        <div className="text-lg font-semibold text-slate-900 mb-1">{t("report_unavailable")}</div>
        <div className="text-sm text-slate-600 mb-6">{t("content_removed")}</div>
        <BackButton className="" testid="detail-back-btn" />
      </div>
    </div>
  );

  if (!report) return (
    <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">{t("loading")}</div></div>
  );

  const color = RISK_COLORS[report.risk_level] || "#9CA3AF";

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="report-detail-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-4 flex flex-wrap items-center justify-between gap-3">
        <BackButton className="" testid="detail-back-btn" />
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${report?.lat},${report?.lng}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="navigate-btn"
          >
            <Button size="sm" variant="outline" className="rounded-full hover:bg-slate-900 hover:text-white">
              <Navigation size={14} className="mr-1.5" /> {t("navigate")}
            </Button>
          </a>
          <ReportDonateButton reportId={id} t={t} />
        </div>
      </div>
      {justCreated && (
        <div className="max-w-6xl mx-auto px-4 md:px-8 mb-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3" data-testid="report-created-banner">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
            <div className="flex-1">
              <div className="font-heading font-bold text-emerald-900">{t("report_sent_b")}</div>
              <div className="text-sm text-emerald-800 mt-0.5">{t("report_sent_desc")}</div>
            </div>
            <Link to="/map" data-testid="back-to-map-btn"><Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800 hover:text-white text-white"><MapIconLucide size={14} className="mr-1.5" /> {t("back_to_map")}</Button></Link>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            {report.photos.length > 0 && (
              <div className="grid grid-cols-2 gap-1 max-h-[420px]">
                {report.photos.slice(0, 4).map((p, i) => (
                  <FullscreenMedia key={i} paths={report.photos} initialIndex={i} testid={`detail-photo-fs-${i}`}>
                    <button className={`relative group ${report.photos.length === 1 ? "col-span-2" : ""}`} data-testid={`detail-photo-${i}`}>
                      <img src={`${BACKEND_URL}/api/files/${p}`} alt={`p${i}`} className="w-full h-[210px] object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="rounded-full bg-white/90 text-slate-900 px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"><Expand size={12} /> {t("fullscreen")}</div>
                      </div>
                    </button>
                  </FullscreenMedia>
                ))}
              </div>
            )}
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Badge style={{ backgroundColor: color, color: "#fff" }} className="capitalize" data-testid="detail-risk-badge">{t("risk_prefix")} {report.risk_level}</Badge>
                <Badge variant="outline" className="capitalize">{t(`cat_${report.category}`) || report.category}</Badge>
                <Badge variant="outline" className="capitalize">{t(`sub_${report.subcategory}`) || report.subcategory.replace(/_/g," ")}</Badge>
                {report.status && !["published"].includes(report.status) && (
                  <Badge variant="outline" className="capitalize" data-testid="detail-status">{t(`st_${report.status}`) || report.status.replace(/_/g," ")}</Badge>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">{report.description}</h1>
              <div className="mt-4 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><MapPin size={12} /> {report.lat.toFixed(4)}, {report.lng.toFixed(4)}</span>
                <span>·</span>
                <span>{t("of_author")} {displayAuthorName(report, t)}</span>
                <span>·</span>
                <span>{fmtDateTime(report.created_at)}</span>
              </div>
              {/* Phase 5 — share only when the report is publicly viewable */}
              {report.status === "published" && (
                <div className="mt-3">
                  <ShareButton reportId={id} title={report.description?.slice(0, 100)} description={""} testid="report-share" />
                </div>
              )}
            </div>
          </div>

          {/* Admin moderation actions (visible only to admin/moderator for pending reports) */}
          {user && (user.role === "admin" || user.role === "moderator") && report.status === "pending_moderation" && (
            <AdminModerationBlock reportId={id} report={report} onDone={load} />
          )}

          {/* Full admin actions bar — Lotto 4 #4: admin/moderator can moderate
              directly from the map without navigating to the Admin Dashboard.
              Rendered for ANY status (including already-published) so admins
              can change color/state, mark sensitive, reopen, or delete.
              Hidden completely from citizens; backend re-checks role. */}
          {user && (user.role === "admin" || user.role === "moderator") && (
            <AdminReportActionsBar
              report={report}
              user={user}
              onDone={(action) => {
                if (action === "deleted") {
                  window.location.href = "/map";
                } else {
                  load();
                }
              }}
            />
          )}

          {/* Admin: PEC dispatch (available for red/yellow/green once approved) */}
          {user && (user.role === "admin" || user.role === "moderator") && report.status && !["pending_moderation", "rejected"].includes(report.status) && (
            <AdminPecBlock reportId={id} report={report} onDone={load} />
          )}

          {/* Additional photos + Add-photo block */}
          {user && <AddPhotoBlock reportId={id} report={report} onAdded={load} t={t} />}

          {/* Phase 4 — Videos (read-only gallery) */}
          {report.videos && report.videos.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold mb-2">{t("video_upload_label")}</div>
              <VideoGallery paths={report.videos} testidPrefix="report-videos" />
            </div>
          )}

          {/* Community actions — replaceable confirmation */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="confirm-block">
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold mb-2">{t("confirm_choose_status")}</div>
            <div className="text-xs text-slate-500 mb-3">
              {(() => {
                const c = report.confirmation_counts || {};
                return `${c.resolved || 0} ${t("confirm_resolved")} · ${c.in_progress || 0} ${t("confirm_in_progress")} · ${c.worse || 0} ${t("confirm_worse")}`;
              })()}
            </div>
            {myConfirmation && (
              <div className="mb-3 text-xs bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 inline-flex items-center gap-1.5" data-testid="my-current-confirmation">
                <CheckCircle2 size={11} className="text-emerald-700" />
                {t("confirm_your_current")}: <b>{t(`confirm_${myConfirmation.kind}`)}</b>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <Button
                data-testid="confirm-resolved"
                onClick={() => confirm("resolved")}
                variant={myConfirmation?.kind === "resolved" ? "default" : "outline"}
                className={`rounded-full min-h-11 h-auto py-2 px-3 whitespace-normal text-xs sm:text-sm text-center leading-tight ${myConfirmation?.kind === "resolved" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "hover:bg-emerald-50 hover:text-emerald-800"}`}
              >
                <CheckCircle2 size={14} className="mr-1.5 shrink-0" />
                <span className="break-words">{t("confirm_resolved")}</span>
              </Button>
              <Button
                data-testid="confirm-in-progress"
                onClick={() => confirm("in_progress")}
                variant={myConfirmation?.kind === "in_progress" ? "default" : "outline"}
                className={`rounded-full min-h-11 h-auto py-2 px-3 whitespace-normal text-xs sm:text-sm text-center leading-tight ${myConfirmation?.kind === "in_progress" ? "bg-blue-600 hover:bg-blue-700 text-white" : "hover:bg-blue-50 hover:text-blue-800"}`}
              >
                <RefreshCw size={14} className="mr-1.5 shrink-0" />
                <span className="break-words">{t("confirm_in_progress")}</span>
              </Button>
              <Button
                data-testid="confirm-worse"
                onClick={() => confirm("worse")}
                variant={myConfirmation?.kind === "worse" ? "default" : "outline"}
                className={`rounded-full min-h-11 h-auto py-2 px-3 whitespace-normal text-xs sm:text-sm text-center leading-tight ${myConfirmation?.kind === "worse" ? "bg-red-600 hover:bg-red-700 text-white" : "hover:bg-red-50 hover:text-red-800"}`}
              >
                <AlertTriangle size={14} className="mr-1.5 shrink-0" />
                <span className="break-words">{t("confirm_worse")}</span>
              </Button>
            </div>
            {myConfirmation && (
              <div className="mt-2 flex justify-end">
                <Button data-testid="confirm-undo" onClick={() => confirm("undo")} variant="ghost" size="sm" className="rounded-full text-xs text-slate-500 hover:bg-slate-100">
                  <XCircle size={12} className="mr-1" /> {t("confirm_undo")}
                </Button>
              </div>
            )}
            {confirmChange && (
              <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="confirm-change-dialog">
                <div className="text-sm text-slate-800">
                  {t("confirm_change_desc").replace("{from}", t(`confirm_${confirmChange.current}`)).replace("{to}", t(`confirm_${confirmChange.newKind}`))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button data-testid="confirm-change-ok" onClick={confirmChangeAccept} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("confirm_change_ok")}</Button>
                  <Button data-testid="confirm-change-cancel" onClick={() => setConfirmChange(null)} size="sm" variant="ghost" className="rounded-full">{t("cancel")}</Button>
                </div>
              </div>
            )}

            {report.category === "reusable" && report.subcategory !== "gift" && (
              <div className="mt-4 space-y-2" data-testid="reusable-block">
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  La disponibilità non è garantita. L'oggetto può essere ritirato da chiunque arrivi per primo.
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    data-testid="reusable-no-longer-present"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/reports/${id}/no-longer-present`);
                        if (data.already_confirmed) toast.info("Hai già confermato");
                        else toast.success(`Grazie! Conferme: ${data.count}${data.auto_resolved ? " — segnalazione risolta" : ""}`);
                        await load();
                      } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
                    }}
                    variant="outline"
                    className="rounded-full hover:bg-slate-900 hover:text-white"
                  >
                    <XCircle size={14} className="mr-1.5" /> Non più presente
                    {typeof report.no_longer_present_count === "number" && report.no_longer_present_count > 0 && (
                      <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-700 px-1.5 rounded-full">{report.no_longer_present_count}</span>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {report.subcategory === "gift" && (
              <GiftStatusBlock report={report} id={id} user={user} onChanged={load} t={t} />
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-4">{t("timeline")}</div>
            <ol className="relative border-l border-emerald-200 space-y-4 pl-5">
              {(report.timeline || []).map((ev, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow" />
                  <div className="text-sm text-slate-800 font-semibold capitalize">{t(`st_${ev.status}`) || ev.status.replace(/_/g," ")}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(ev.at)}</div>
                  {ev.note && <div className="text-xs text-slate-600 mt-1">{ev.note}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Chat side */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm sticky top-24 overflow-hidden flex flex-col h-[70vh]">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <MessagesSquare size={16} className="text-emerald-700" />
              <div className="font-heading font-semibold">{t("coordinate")}</div>
              <div className="ml-auto">
                <CommentSortSelect value={commentSort} onChange={setCommentSort} testid="report-comment-sort" />
              </div>
            </div>
              <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/40" data-testid="chat-list">
              {messages.length === 0 && <div className="text-center text-xs text-slate-500 py-8">{t("no_messages")}</div>}
              {messages.map((m) => (
                <ChatMessageRow key={m.message_id} m={m} user={user} onEdited={load} reportId={id} t={t} lang={lang} />
              ))}
            </div>
            <div className="p-3 border-t border-slate-200 bg-white">
              {user ? (
                <div className="flex items-center gap-2">
                  <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("write_message")} className="h-10 rounded-full" />
                  <Button data-testid="chat-send" onClick={send} size="icon" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white h-10 w-10"><Send size={16} /></Button>
                </div>
              ) : (
                <Link to="/auth" className="block text-center text-sm text-emerald-700 hover:underline">{t("login_to_join")}</Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GiftStatusBlock({ report, id, user, onChanged, t }) {
  const isOwner = user && user.user_id === report.author_id;
  const isAdmin = user && user.role === "admin";
  const canManage = isOwner || isAdmin;
  const status = report.gift_status || "available";
  const setStatus = async (s) => {
    try {
      await api.post(`/reports/${id}/gift-status`, { status: s });
      toast.success("Stato aggiornato");
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const STATUSES = [
    { k: "available", label: "Disponibile", color: "bg-emerald-100 text-emerald-800" },
    { k: "in_agreement", label: "In accordo", color: "bg-blue-100 text-blue-800" },
    { k: "delivered", label: "Consegnato", color: "bg-slate-200 text-slate-800" },
    { k: "unavailable", label: "Non più disponibile", color: "bg-red-100 text-red-800" },
  ];
  const current = STATUSES.find((s) => s.k === status) || STATUSES[0];
  const closed = status === "delivered" || status === "unavailable";
  return (
    <div className="mt-4 space-y-2" data-testid="gift-block">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("announcement_status")}</div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-3 h-8 rounded-full inline-flex items-center gap-1 font-semibold ${current.color}`} data-testid="gift-current-status">{current.label}</span>
        {canManage && !closed && (
          <>
            <Button data-testid="gift-set-in-agreement" onClick={() => setStatus("in_agreement")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-blue-50 hover:text-blue-800">In accordo</Button>
            <Button data-testid="gift-set-delivered" onClick={() => setStatus("delivered")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-900 hover:text-white">{t("gift_delivered")}</Button>
            <Button data-testid="gift-set-unavailable" onClick={() => setStatus("unavailable")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-red-50 hover:text-red-800">{t("gift_unavailable")}</Button>
          </>
        )}
        {canManage && closed && (
          <Button data-testid="gift-reopen" onClick={() => setStatus("available")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-emerald-50 hover:text-emerald-800">{t("gift_reopen")}</Button>
        )}
      </div>
      {closed && (
        <div className="text-xs text-slate-600 italic" data-testid="gift-closed-notice">Questo annuncio è chiuso. La cronologia della chat resta visibile ma non sono ammessi nuovi messaggi di richiesta.</div>
      )}
    </div>
  );
}

function ChatMessageRow({ m, user, onEdited, reportId, t, lang }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  const [busy, setBusy] = useState(false);
  const [translated, setTranslated] = useState(null); // { text, source_lang }
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const isOwn = user && user.user_id === m.author_id;
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/reports/${reportId}/messages/${m.message_id}`, { text: text.trim() });
      setEditing(false);
      onEdited && onEdited();
      toast.success(t("message_edited"));
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setBusy(false); }
  };
  const doTranslate = async () => {
    if (showTranslation) { setShowTranslation(false); return; }
    if (translated) { setShowTranslation(true); return; }
    setTranslating(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/messages/${m.message_id}/translate`, { target_lang: lang || "it" });
      setTranslated({ text: data.translation, source_lang: data.source_lang });
      setShowTranslation(true);
    } catch (e) {
      toast.error(t("chat_translate_error"));
    } finally { setTranslating(false); }
  };
  return (
    <div className="flex items-start gap-2 group" data-testid={`msg-${m.message_id}`}>
      <Avatar className="w-8 h-8 shrink-0">
        <AvatarImage src={m.author_id_anon ? undefined : m.author_picture} />
        <AvatarFallback className="bg-emerald-100 text-emerald-800 text-xs">{displayAuthorName(m, t)?.[0]?.toUpperCase() || "?"}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <b className="text-slate-900">{displayAuthorName(m, t)}</b>
          <span>·</span>
          <span>{new Date(m.created_at).toLocaleTimeString()}</span>
          {m.edited && <span className="italic text-slate-400" data-testid={`msg-edited-${m.message_id}`}>{t("message_edited")}</span>}
        </div>
        {editing ? (
          <div className="mt-1 space-y-2">
            <Textarea data-testid={`msg-edit-input-${m.message_id}`} value={text} onChange={(e) => setText(e.target.value)} className="rounded-xl text-sm" />
            <div className="flex gap-2">
              <Button data-testid={`msg-save-${m.message_id}`} onClick={save} disabled={busy} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white h-8 text-xs">{t("save")}</Button>
              <Button data-testid={`msg-cancel-${m.message_id}`} onClick={() => { setEditing(false); setText(m.text); }} variant="ghost" size="sm" className="rounded-full hover:bg-slate-100 hover:text-slate-900 h-8 text-xs">{t("cancel")}</Button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="text-sm text-slate-800 leading-snug bg-white border border-slate-200 rounded-2xl rounded-tl-md p-3 mt-1 break-words" data-testid={`msg-original-${m.message_id}`}>{m.text}</div>
            {showTranslation && translated?.text && (
              <div className="mt-1.5 text-sm text-slate-800 leading-snug bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tl-md p-3 break-words" data-testid={`msg-translated-${m.message_id}`}>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1 flex items-center gap-1">
                  <Sparkles size={10} /> {t("chat_translated_badge")}
                </div>
                {translated.text}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <CommentLikeButton
                targetKind="report_message"
                targetId={m.message_id}
                initialCount={m.like_count || 0}
                initialLiked={!!m.liked_by_me}
                testid={`msg-like-${m.message_id}`}
              />
              <button
                data-testid={`msg-translate-${m.message_id}`}
                onClick={doTranslate}
                disabled={translating}
                className="text-[11px] px-2 h-6 rounded-full border border-slate-200 text-slate-500 hover:text-emerald-700 hover:border-emerald-300 disabled:opacity-50"
              >
                {translating ? t("chat_translating") : (showTranslation ? t("chat_show_original") : t("chat_translate"))}
              </button>
              {!isOwn && user && (
                <ReportContentButton
                  targetType="message"
                  targetId={m.message_id}
                  label=""
                  testid={`msg-content-report-${m.message_id}`}
                  className="h-6 px-2 text-[11px] opacity-0 group-hover:opacity-100"
                />
              )}
              {isOwn && (
                <button data-testid={`msg-edit-${m.message_id}`} onClick={() => { setEditing(true); setText(m.text); }} className="text-[11px] px-2 h-6 rounded-full border border-slate-200 text-slate-500 hover:text-emerald-700 hover:border-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("edit_message")}
                </button>
              )}
              {!isOwn && user && (
                <ReportUserButton
                  reportedUserId={m.author_id}
                  reportedUserName={displayAuthorName(m, t)}
                  contextType="message"
                  contextRef={`${reportId}:${m.message_id}`}
                  label=""
                  testid={`msg-report-user-${m.message_id}`}
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[11px] opacity-0 group-hover:opacity-100"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportDonateButton({ reportId, t }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(5);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const donate = async () => {
    const val = parseFloat(amount);
    if (!val || val < 0.5) { toast.error(t("donate_min_error")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: val,
        currency: "eur",
        target_type: "report",
        target_id: reportId,
        origin_url: window.location.origin,
        message: msg,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || t("donate_stripe_error")); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="donate-report-btn" size="sm" className="rounded-full bg-rose-500 hover:bg-rose-600 hover:text-white text-white">
          <Heart size={14} className="mr-1.5" /> {t("donate_short")}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="donate-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("donate_report_title")}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600 leading-relaxed">
          {t("donate_report_desc_p1")} <b>{t("donate_report_desc_b1")}</b>{t("donate_report_desc_p2")} <b>{t("donate_report_desc_b2")}</b>{t("donate_report_desc_p3")} <b>{t("donate_report_desc_b3")}</b>.
        </div>
        <div className="grid grid-cols-6 gap-2 mt-2">
          {[0.5, 2, 5, 10, 25, 50].map((v) => (
            <button key={v} type="button" data-testid={`donate-report-preset-${v}`} onClick={() => setAmount(v)} className={`h-11 rounded-full font-semibold text-xs ${amount === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>€{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs text-slate-500 whitespace-nowrap">{t("donate_free_offer")}</label>
          <Input data-testid="donate-report-amount" type="number" min="0.50" step="0.10" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl text-base font-semibold" />
        </div>
        <Textarea data-testid="donate-report-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t("donate_report_placeholder")} className="rounded-xl" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full hover:bg-slate-100 hover:text-slate-900">{t("cancel")}</Button>
          <Button data-testid="donate-report-submit" onClick={donate} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">{busy ? t("donate_redirect") : `${t("donate_now")} €${amount}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPhotoBlock({ reportId, report, onAdded, t }) {
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null); // { path, url }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending({ path: data.path, url: `${BACKEND_URL}${data.url}` });
    } catch (e) { toast.error(t("photo_upload_failed")); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/photos`, { path: pending.path, note });
      if (data?.photo?.status === "pending_moderation") toast.success(t("add_photo_pending"));
      else toast.success(t("add_photo_success"));
      setPending(null); setNote("");
      onAdded && onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || t("add_photo_failed")); }
    finally { setSaving(false); }
  };

  const extras = report.additional_photos || [];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="add-photo-block">
      <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold mb-2 flex items-center gap-2">
        <Camera size={12} /> {t("add_photo")}
      </div>
      <div className="text-sm text-slate-600 mb-4">{t("add_photo_desc")}</div>

      {extras.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("additional_photos")} ({extras.length})</div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {extras.map((p) => (
              <div key={p.photo_id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`extra-photo-${p.photo_id}`}>
                <img src={`${BACKEND_URL}/api/files/${p.path}`} alt="extra" className={`w-full h-full object-cover ${p.status === "pending_moderation" ? "opacity-60" : ""}`} />
                {p.status === "pending_moderation" && (
                  <div className="absolute top-1 left-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800">{t("pending_review")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!pending ? (
        <div className="grid grid-cols-2 gap-2 max-w-md">
          <label className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700" data-testid="detail-take-photo">
            {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
            <span className="text-[11px] mt-1 font-semibold text-center px-1">{t("take_photo")}</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          </label>
          <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-slate-500 hover:text-emerald-700" data-testid="detail-gallery-photo">
            {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
            <span className="text-[11px] mt-1 text-center px-1">{t("from_gallery")}</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          </label>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border border-slate-200 max-w-md">
            <img src={pending.url} alt="preview" className="w-full h-48 object-cover" />
            <button onClick={() => setPending(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black" data-testid="detail-photo-remove"><X size={14} /></button>
          </div>
          <Textarea data-testid="detail-photo-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("add_photo_note_placeholder")} className="rounded-xl max-w-md" />
          <div className="flex gap-2">
            <Button data-testid="detail-photo-submit" onClick={submit} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 size={14} className="mr-2" />}
              {t("add_photo_confirm")}
            </Button>
            <Button variant="ghost" onClick={() => { setPending(null); setNote(""); }} className="rounded-full hover:bg-slate-100 hover:text-slate-900">{t("cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}



// -----------------------------------------------------------------------------
// Admin moderation panel — visible in the report detail for admin/moderator when
// the report is pending_moderation. Mirrors the inline approve/reject UX of the
// notifications bell so an admin who lands on the detail page can act directly.
// -----------------------------------------------------------------------------
function AdminModerationBlock({ reportId, report, onDone }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(null); // {message, current_status}
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [scope, setScope] = useState("content");

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${reportId}/approve`, {});
      if (data.ok) {
        toast.success("Segnalazione approvata");
        await onDone();
      } else {
        toast.error(data.message || "Errore");
        setHandled({ message: data.message || "Contenuto già gestito", current_status: data.current_status });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Motivazione obbligatoria"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${reportId}/reject`, {
        reason: reason.trim(), note: note.trim() || null, scope,
      });
      if (data.ok) {
        toast.success(scope === "photo_only" ? "Foto rifiutata, autore avvisato" : "Segnalazione rifiutata");
        setRejectOpen(false); setReason(""); setNote(""); setScope("content");
        await onDone();
      } else {
        toast.error(data.message || "Errore");
        setHandled({ message: data.message || "Contenuto già gestito", current_status: data.current_status });
        setRejectOpen(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 md:p-6 shadow-sm"
      data-testid="admin-mod-block"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
          <ShieldCheck size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">{t("admin_moderation_label")}</div>
          <div className="font-heading font-bold text-slate-900 text-base md:text-lg mt-0.5">
            Questa segnalazione è in attesa di revisione
          </div>
          <div className="text-xs text-amber-900/80 mt-1">
            Decidi se pubblicarla o rifiutarla. Le tue azioni vengono registrate nel log admin.
          </div>
          {handled ? (
            <div
              className="mt-3 rounded-xl bg-slate-100 border border-slate-200 px-3 py-2 text-xs text-slate-700"
              data-testid="admin-mod-handled"
            >
              {handled.message}
              {handled.current_status ? ` — stato attuale: ${handled.current_status}` : ""}
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="admin-mod-approve"
                onClick={approve}
                disabled={busy}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white disabled:opacity-60"
              >
                <ThumbsUp size={14} className="mr-1.5" /> Approva
              </Button>
              <Button
                data-testid="admin-mod-reject"
                onClick={() => setRejectOpen(true)}
                disabled={busy}
                variant="outline"
                className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 disabled:opacity-60"
              >
                <ThumbsDown size={14} className="mr-1.5" /> Rifiuta
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={(v) => { if (!busy) setRejectOpen(v); }}>
        <DialogContent data-testid="admin-mod-reject-dialog">
          <DialogHeader>
            <DialogTitle>{t("reject_reason_title")}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500">
            La motivazione è obbligatoria. La nota è facoltativa e non sarà mostrata all'autore.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Ambito</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="admin-mod-scope-photo"
                onClick={() => setScope("photo_only")}
                className={`h-9 rounded-full text-xs font-semibold border ${scope === "photo_only" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
              >{t("only_photo")}</button>
              <button
                type="button"
                data-testid="admin-mod-scope-content"
                onClick={() => setScope("content")}
                className={`h-9 rounded-full text-xs font-semibold border ${scope === "content" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200"}`}
              >{t("entire_report")}</button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Motivazione *</div>
            <Textarea
              data-testid="admin-mod-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t("reject_photo_placeholder")}
              className="rounded-xl"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Nota (interna, facoltativa)</div>
            <Textarea
              data-testid="admin-mod-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={t("admin_log_note")}
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="admin-mod-reject-cancel"
              variant="outline"
              className="rounded-full hover:bg-slate-100 hover:text-slate-900"
              onClick={() => setRejectOpen(false)}
              disabled={busy}
            >{t("cancel")}</Button>
            <Button
              data-testid="admin-mod-reject-submit"
              onClick={submitReject}
              disabled={busy || !reason.trim()}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white hover:text-white disabled:opacity-60"
            >{busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null} Conferma rifiuto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Admin — Invio PEC al Comune (manual flow, no auto-send, no simulated receipts)
// -----------------------------------------------------------------------------
function AdminPecBlock({ reportId, report, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [dispatch, setDispatch] = useState(null);
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadExisting = async () => {
    try { const { data } = await api.get(`/admin/pec/dispatches`, { params: { report_id: reportId } }); setExisting(data || []); } catch {}
  };
  React.useEffect(() => { loadExisting(); }, [reportId]);

  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${reportId}/pec/prepare`, {});
      setDispatch(data); setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const updateField = (patch) => setDispatch((d) => ({ ...d, ...patch }));
  const updateRecipient = (patch) => setDispatch((d) => ({ ...d, recipient: { ...(d.recipient || {}), ...patch } }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        recipient_comune: dispatch.recipient?.comune,
        recipient_ufficio: dispatch.recipient?.ufficio,
        recipient_pec: dispatch.recipient?.pec,
        recipient_country_code: dispatch.recipient?.country_code,
        subject: dispatch.subject,
        body: dispatch.body,
        photos_included: dispatch.photos_included,
        photos_excluded: dispatch.photos_excluded,
      };
      const { data } = await api.put(`/admin/pec/dispatches/${dispatch.dispatch_id}`, payload);
      setDispatch(data);
      toast.success("Salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/pec/dispatches/${dispatch.dispatch_id}/send`, {});
      if (data.ok) toast.success("PEC inviata");
      else toast.error(data.message || "Invio non effettuato");
      // update local dispatch state
      setDispatch((d) => ({ ...d, state: data.state }));
      setConfirmOpen(false);
      loadExisting();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const cancelDispatch = async () => {
    if (!window.confirm("Annullare la preparazione PEC?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/pec/dispatches/${dispatch.dispatch_id}/cancel`, {});
      setOpen(false); setDispatch(null); loadExisting();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const togglePhotoExcluded = (p) => {
    setDispatch((d) => {
      const excluded = new Set(d.photos_excluded || []);
      if (excluded.has(p)) excluded.delete(p); else excluded.add(p);
      const included = (report.photos || []).filter((x) => !excluded.has(x));
      return { ...d, photos_excluded: Array.from(excluded), photos_included: included };
    });
  };

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5" data-testid="admin-pec-block">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0"><Send size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold">Inoltro formale al Comune (PEC)</div>
          <div className="font-heading font-bold text-slate-900 text-base mt-0.5">{t("admin_no_auto_dispatch")}</div>
          <div className="text-xs text-blue-900/80 mt-1">L'invio parte solo dopo la tua conferma esplicita. Anche in caso di segnalazione rossa, nulla viene inviato in automatico.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="pec-prepare-btn" onClick={prepare} disabled={busy || open} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white hover:text-white">
              <Send size={14} className="mr-1.5" /> Invia PEC al Comune
            </Button>
            {existing.length > 0 && (
              <div className="text-xs text-slate-500 self-center">Preparazioni precedenti: {existing.length}</div>
            )}
          </div>
        </div>
      </div>

      {open && dispatch && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="pec-editor">
          {dispatch.config_missing && (
            <div className="rounded-xl bg-amber-50 border border-amber-300 p-2 text-xs text-amber-900" data-testid="pec-config-missing">
              {t("admin_pec_required_msg")}
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Paese (ISO2)</div>
              <input data-testid="pec-recipient-country" value={dispatch.recipient?.country_code || ""} onChange={(e) => updateRecipient({ country_code: e.target.value.toUpperCase().slice(0, 2) })} placeholder="IT, FR, DE…" maxLength={2} className="w-full h-9 rounded-full px-3 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Comune / Località</div>
              <input data-testid="pec-recipient-comune" value={dispatch.recipient?.comune || ""} onChange={(e) => updateRecipient({ comune: e.target.value })} className="w-full h-9 rounded-full px-3 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ufficio</div>
              <input data-testid="pec-recipient-ufficio" value={dispatch.recipient?.ufficio || ""} onChange={(e) => updateRecipient({ ufficio: e.target.value })} placeholder="Ambiente / Protezione Civile / …" className="w-full h-9 rounded-full px-3 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Recapito certificato</div>
              <input data-testid="pec-recipient-pec" value={dispatch.recipient?.pec || ""} onChange={(e) => updateRecipient({ pec: e.target.value })} placeholder="pec, eDelivery o email istituzionale" className="w-full h-9 rounded-full px-3 border border-slate-200 text-sm" />
            </div>
          </div>
          {dispatch.lookup_notice && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-2 text-xs text-blue-900" data-testid="pec-lookup-notice">
              {dispatch.lookup_notice}
            </div>
          )}
          <div className="text-[11px] text-slate-500">
            Fonte destinatario: <b data-testid="pec-source-badge">{dispatch.recipient?.source === "ipa" ? "IPA (Indice PA)" : "inserimento manuale"}</b>
          </div>
          {(dispatch.ipa_candidates || []).length > 0 && (
            <div className="text-xs text-slate-600" data-testid="pec-ipa-candidates">
              <b>Candidati IPA:</b>{" "}
              {dispatch.ipa_candidates.map((c, i) => (
                <button key={i} onClick={() => updateRecipient({ comune: c.denominazione, pec: c.pec, ufficio: c.ufficio || null })} className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800">
                  {c.denominazione} · {c.pec}
                </button>
              ))}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Oggetto</div>
            <input data-testid="pec-subject" value={dispatch.subject || ""} onChange={(e) => updateField({ subject: e.target.value })} className="w-full h-10 rounded-xl px-3 border border-slate-200 text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Testo</div>
            <Textarea data-testid="pec-body" rows={8} value={dispatch.body || ""} onChange={(e) => updateField({ body: e.target.value })} className="rounded-xl text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Fotografie allegate (clicca per escludere)</div>
            <div className="grid grid-cols-4 gap-2">
              {(report.photos || []).map((p, i) => {
                const excluded = (dispatch.photos_excluded || []).includes(p);
                return (
                  <button key={i} onClick={() => togglePhotoExcluded(p)} data-testid={`pec-photo-${i}`} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${excluded ? "border-red-400 opacity-40" : "border-emerald-400"}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                    {excluded && <div className="absolute inset-0 flex items-center justify-center bg-red-500/40 text-white font-bold text-xs">ESCLUSA</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button data-testid="pec-save-btn" onClick={save} disabled={busy} variant="outline" className="rounded-full">Salva modifiche</Button>
            <Button data-testid="pec-send-btn" onClick={() => setConfirmOpen(true)} disabled={busy || !dispatch.recipient?.pec || !dispatch.subject || !dispatch.body} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white hover:text-white">
              Anteprima e conferma
            </Button>
            <Button data-testid="pec-cancel-btn" onClick={cancelDispatch} disabled={busy} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">Annulla preparazione</Button>
            <span className="text-xs text-slate-500 ml-auto">Stato: <b>{dispatch.state}</b></span>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-2xl" data-testid="pec-confirm-dialog">
          <DialogHeader><DialogTitle>Anteprima PEC — conferma finale</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">{t("no_pec_before_button")}</div>
          <div className="space-y-2 text-sm">
            <div><b>A:</b> {dispatch?.recipient?.pec || "—"} ({dispatch?.recipient?.comune || "—"}{dispatch?.recipient?.ufficio ? ` · ${dispatch?.recipient?.ufficio}` : ""})</div>
            <div><b>Oggetto:</b> {dispatch?.subject}</div>
            <div className="bg-slate-50 p-2 rounded-lg max-h-[280px] overflow-y-auto whitespace-pre-line text-xs">{dispatch?.body}</div>
            <div className="text-xs"><b>Allegati inclusi:</b> {(dispatch?.photos_included || []).length}, esclusi: {(dispatch?.photos_excluded || []).length}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button data-testid="pec-confirm-send" onClick={send} disabled={busy} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white hover:text-white">
              {dispatch?.config_missing ? "Conferma (bloccato: config mancante)" : "Conferma e invia PEC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

